import { useState, useRef, useEffect, forwardRef } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Autocomplete, CircularProgress, TextField, createFilterOptions } from "@mui/material";
import { debounce } from "lodash";
import ToasterWithMessage from "../../../components/toaster";
import { ALL_SUBJECTS, INSERT_SUBJECTS } from "../../../components/Popups/api/FileUploadQuery";
import { getScreenDimensions } from "../../../utils/helper";

export interface InsertSubjectsVariables {
  sjName: string;
}

export interface InsertSubjectsResponse {
  insertSubjects: string;
}

interface SubjectOption {
  id?: string;
  inputValue?: string;
  title: string;
}

interface AllSubjectsData {
  allSubjects: {
    nodes: {
      sjName: string;
      sjOriginalName: string;
      sjId: string;
    }[];
    totalCount: number;
  };
}

interface AllSubjectsVariables {
  sjName: string;
}

interface SubjectAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

const filter = createFilterOptions<SubjectOption>();
const { error: errorToast } = ToasterWithMessage();

const SubjectAutocomplete = forwardRef<HTMLInputElement, SubjectAutocompleteProps>(
  ({ value, onChange, placeholder }, ref) => {
    const [options, setOptions] = useState<SubjectOption[]>([]);
    const lastValidValueRef = useRef(value);
    const { isMobileScreen } = getScreenDimensions();

    const [getSubjects, { loading }] = useLazyQuery<AllSubjectsData, AllSubjectsVariables>(ALL_SUBJECTS, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const seenTitles = new Set<string>();

        setOptions(
          data?.allSubjects?.nodes
            ?.map((subject) => (subject.sjOriginalName ? { title: subject.sjOriginalName } : null))
            ?.filter(Boolean)
            ?.filter((item) => {
              if (seenTitles.has(item!.title)) {
                return false;
              }
              seenTitles.add(item!.title);
              return true;
            }) as { title: string }[]
        );
      },
      onError: (error) => {
        errorToast(error.message);
      },
    });

    const [insertSubject] = useMutation<InsertSubjectsResponse, InsertSubjectsVariables>(INSERT_SUBJECTS, {
      onCompleted: (data) => {
        console.log("Insert subject result:", data);
      },
      onError: (error) => {
        console.error("Insert subject error:", error);
        // errorToast(error.message);
      },
    });

    const debouncedSearch = useRef(
      debounce((searchTerm: string) => {
        getSubjects({ variables: { sjName: `${searchTerm}%` } });
      }, 500)
    ).current;

    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    return (
      <Autocomplete
        value={value ? { title: value } : null}
        onChange={async (_event, newValue) => {
          if (typeof newValue === "string") {
            // User typed a new string (freeSolo)
            onChange(newValue);
          } else if (newValue && newValue.inputValue) {
            // User selected the "Add ..." option.
            onChange(newValue.inputValue);
            lastValidValueRef.current = newValue.inputValue;
            await insertSubject({ variables: { sjName: newValue.inputValue } });
          } else if (newValue) {
            // User selected an option from the list.
            onChange(newValue.title);
            lastValidValueRef.current = newValue.title;
          } else {
            onChange("");
            lastValidValueRef.current = "";
          }
        }}
        onInputChange={(_event, value) => {
          onChange(value);
          if (value.length > 2) {
            debouncedSearch(value);
          }
        }}
        selectOnFocus
        handleHomeEndKeys
        loading={loading}
        onBlur={() => {
          if (value !== lastValidValueRef.current) {
            onChange(lastValidValueRef.current);
          }
          debouncedSearch(value);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          // If the user typed something that isn’t already an option,
          // add an option to let them add that new value.
          const isExisting = options.some((option) => inputValue.toLowerCase() === option.title.toLowerCase());
          if (inputValue !== "" && !isExisting) {
            filtered.push({
              inputValue,
              title: `Add "${inputValue}"`,
            });
          }
          return filtered;
        }}
        componentsProps={{
          popper: {
            sx: {
              "& .MuiAutocomplete-noOptions": {
                display: "none",
              },
              "& .MuiAutocomplete-paper": {
                display: loading || value.length <= 2 ? "none" : undefined,
              },
              "& .MuiAutocomplete-paper .MuiAutocomplete-listbox": {
                padding: 0,
                overflowX: "scroll",
              },
              "& .MuiAutocomplete-paper .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                whiteSpace: "nowrap",
                minWidth: "fit-content",
              }
            },
          },
        }}
        options={options.length > 0 ? options : [{ title: "No options", id: "no-options" }]}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.title)}
        sx={{
          ".MuiOutlinedInput-root": {
            paddingRight: "30px !important",
            fontWeight: 500,
            fontFamily: "var(--font-inter)",
            fontSize: "var(--font-size-xs)",
            backgroundColor: `${isMobileScreen ? "var(--color-whitesmoke-200)" : "var(--color-white)"}`,
            borderRadius: "var(--br-3xs)",
            height: `${isMobileScreen ? "41px" : "24px"}`,
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
          },
          ".MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          ".MuiAutocomplete-popupIndicator": {
            display: "none",
          },
          width: "100%",
          Padding: 0,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            inputRef={ref}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={15} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }
);

export default SubjectAutocomplete;
